import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCancelEstimate, useDeleteEstimate } from '@/hooks/useEstimates';
import { estimateDeleteIsRisky } from '@/types/estimate';
import { Loader2 } from 'lucide-react';

export type EstimateConfirmAction = 'cancel' | 'delete';

interface EstimateActionConfirmDialogProps {
  action: EstimateConfirmAction | null;
  estimate: { id: string; estimate_number: string; status: string } | null;
  onOpenChange: (open: boolean) => void;
  /** Called after the mutation succeeds (e.g. close the parent sheet on delete). */
  onDone?: (action: EstimateConfirmAction) => void;
}

/**
 * Office-side "Cancel estimate" / "Delete estimate" confirmation.
 * Cancel withdraws a draft / sent / viewed estimate (customer link stops
 * working). Delete removes any estimate for good; for a signed, accepted one
 * the copy says what is lost and only users who can see company financials are
 * offered the action.
 */
export function EstimateActionConfirmDialog({ action, estimate, onOpenChange, onDone }: EstimateActionConfirmDialogProps) {
  const cancelEstimate = useCancelEstimate();
  const deleteEstimate = useDeleteEstimate();
  const busy = cancelEstimate.isPending || deleteEstimate.isPending;
  const open = !!action && !!estimate;

  const handleConfirm = async () => {
    if (!action || !estimate) return;
    try {
      if (action === 'cancel') {
        await cancelEstimate.mutateAsync({ estimateId: estimate.id });
      } else {
        await deleteEstimate.mutateAsync({ estimateId: estimate.id });
      }
      onOpenChange(false);
      onDone?.(action);
    } catch {
      // Toast raised by the hook; keep the dialog open so the user can retry or back out.
    }
  };

  const wasSent = estimate?.status === 'sent' || estimate?.status === 'viewed';
  const signedAway = !!estimate && estimateDeleteIsRisky(estimate.status);

  return (
    <AlertDialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {action === 'delete' ? 'Delete estimate' : 'Cancel estimate'}
            {estimate ? ` ${estimate.estimate_number}` : ''}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {action === 'delete'
              ? signedAway
                ? 'This estimate was accepted and signed by the customer. Deleting it removes the quote, the signature and its audit trail for good. This cannot be undone.'
                : 'This permanently removes the estimate and cannot be undone. Use this to clean up a mistake.'
              : wasSent
                ? 'The customer\u2019s accept/decline link will stop working and the estimate is marked cancelled. It stays on record; you can delete it afterwards if it was a mistake.'
                : 'The estimate is marked cancelled and can no longer be sent. It stays on record; you can delete it afterwards if it was a mistake.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Keep estimate</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            disabled={busy}
            className={action === 'delete' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {action === 'delete' ? 'Delete permanently' : 'Cancel estimate'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
